import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { useUser } from '../../app/session';
import { TeamBadge } from '../../components/TeamBadge';
import { toast } from '../../components/toasts';
import { Badge, Button, Empty, ErrorText, Field, Input, Modal, Select, Spinner, Textarea } from '../../components/ui';
import { del, errorMessage, get, post, put } from '../../lib/api';
import { fillQuickReply, QUICK_REPLY_VARIABLES, QuickReply } from '../../lib/quick-replies';
import { Team } from '../../lib/types';

type Draft = Omit<QuickReply, 'id'> & { id?: string };
const EMPTY: Draft = { shortcut: '', title: '', body: '', teamId: null, active: true };

/** Administração → Respostas rápidas: mensagens prontas do compositor ("/" + atalho). */
export function QuickRepliesAdmin() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['quick-replies', 'manage'], queryFn: () => get<QuickReply[]>('/quick-replies/manage') });
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => get<Team[]>('/teams') }).data ?? [];
  const [editing, setEditing] = useState<Draft | null>(null);
  const teamName = (id: string | null) => teams.find((t) => t.id === id)?.name ?? null;

  const remove = async (r: QuickReply) => {
    if (!window.confirm(`Apagar a resposta /${r.shortcut}? Para só tirar do compositor, desative.`)) return;
    try {
      await del(`/quick-replies/${r.id}`);
      void qc.invalidateQueries({ queryKey: ['quick-replies'] });
      toast('Resposta apagada', 'ok');
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  return (
    <div className="max-w-4xl space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-fg-soft">
          Mensagens prontas que o operador insere digitando <b>/</b> no compositor. As variáveis são preenchidas na hora, e o operador revisa antes de enviar.
        </p>
        <Button variant="primary" onClick={() => setEditing({ ...EMPTY })}>
          <Plus className="size-4" /> Nova resposta
        </Button>
      </div>
      {list.isLoading && <Spinner />}
      <ErrorText error={list.error} />
      {list.data && list.data.length === 0 && <Empty>Nenhuma resposta cadastrada.</Empty>}
      {list.data && list.data.length > 0 && (
        <table className="w-full overflow-hidden rounded-lg border border-border bg-panel text-sm">
          <thead className="bg-muted text-left text-xs text-fg-soft">
            <tr>
              <th className="px-3 py-2">Atalho</th>
              <th className="px-3 py-2">Nome e texto</th>
              <th className="px-3 py-2">Equipe</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {list.data.map((r) => (
              <tr key={r.id} className={`border-t border-border align-top ${r.active ? '' : 'opacity-60'}`}>
                <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">/{r.shortcut}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5 font-medium">
                    {r.title}
                    {!r.active && <Badge tone="warn">desativada</Badge>}
                  </div>
                  <div className="line-clamp-2 text-xs whitespace-pre-line text-fg-soft">{r.body}</div>
                </td>
                <td className="px-3 py-2">{r.teamId ? <TeamBadge name={teamName(r.teamId)} /> : <span className="text-xs text-fg-soft">todas</span>}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setEditing({ ...r })} title="Editar">
                    <Pencil className="size-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void remove(r)} title="Apagar">
                    <Trash2 className="size-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && <QuickReplyModal draft={editing} teams={teams} onClose={() => setEditing(null)} />}
    </div>
  );
}

function QuickReplyModal({ draft, teams, onClose }: { draft: Draft; teams: Team[]; onClose: () => void }) {
  const qc = useQueryClient();
  const user = useUser();
  const [d, setD] = useState<Draft>(draft);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  /** Insere a variável onde está o cursor do texto. */
  const insertVariable = (name: string) => {
    const el = bodyRef.current;
    const token = `{${name}}`;
    const start = el?.selectionStart ?? d.body.length;
    const end = el?.selectionEnd ?? d.body.length;
    setD({ ...d, body: d.body.slice(0, start) + token + d.body.slice(end) });
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = { shortcut: d.shortcut, title: d.title, body: d.body, teamId: d.teamId || null, active: d.active };
      if (d.id) await put(`/quick-replies/${d.id}`, body);
      else await post('/quick-replies', body);
      void qc.invalidateQueries({ queryKey: ['quick-replies'] });
      toast('Resposta salva', 'ok');
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const preview = fillQuickReply(d.body, { contactName: 'Maria Silva', operatorName: user.name, ticketCode: 'FLEX-908' });

  return (
    <Modal
      open
      title={d.id ? 'Editar resposta rápida' : 'Nova resposta rápida'}
      onClose={onClose}
      width="max-w-2xl"
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={saving} disabled={!d.shortcut.trim() || !d.title.trim() || !d.body.trim()} onClick={() => void save()}>
            Salvar
          </Button>
        </>
      }
    >
      <div className="grid gap-x-3 sm:grid-cols-[10rem_1fr]">
        <Field label="Atalho" hint="(depois da /)">
          <Input value={d.shortcut} onChange={(e) => setD({ ...d, shortcut: e.target.value })} placeholder="boleto" maxLength={40} />
        </Field>
        <Field label="Nome">
          <Input value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} placeholder="Segunda via do boleto" maxLength={80} />
        </Field>
      </div>
      <Field label="Texto">
        <Textarea ref={bodyRef} rows={6} value={d.body} onChange={(e) => setD({ ...d, body: e.target.value })} maxLength={4000} placeholder="{saudacao}, {primeiro_nome}! ..." />
      </Field>
      <div className="-mt-2 mb-3 flex flex-wrap gap-1">
        {QUICK_REPLY_VARIABLES.map((v) => (
          <button key={v.name} type="button" className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] hover:bg-accent/15" title={v.help} onClick={() => insertVariable(v.name)}>
            {`{${v.name}}`}
          </button>
        ))}
      </div>
      {d.body.trim() && (
        <div className="mb-3 rounded-md bg-accent/5 p-2 text-sm">
          <div className="mb-1 text-[11px] text-fg-soft">Exemplo (cliente Maria Silva, chamado FLEX-908):</div>
          <p className="whitespace-pre-line">{preview}</p>
        </div>
      )}
      <div className="grid gap-x-3 sm:grid-cols-2">
        <Field label="Equipe" hint="(quem vê no compositor)">
          <Select value={d.teamId ?? ''} onChange={(e) => setD({ ...d, teamId: e.target.value || null })}>
            <option value="">Todas as equipes</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Field>
        <label className="flex items-center gap-2 text-sm sm:mt-6">
          <input type="checkbox" checked={d.active} onChange={(e) => setD({ ...d, active: e.target.checked })} />
          Ativa (aparece no compositor)
        </label>
      </div>
      <ErrorText error={error} />
    </Modal>
  );
}
